import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Dropdown, Flex, Space, Tooltip } from 'antd'
import invoiceApi from 'api/invoiceApi'
import { setAppSpinning, showNotification } from 'app/appSlice'
import dayjs from 'dayjs'
import { goodsQueries } from 'features/Category/screens/GoodsScreen/queries'
import IssueInvoicesFromPosModal from 'features/Invoice/components/modals/ModalIssueInvoicesFromPos'
import ModalListInvoiceFromPOS from 'features/Invoice/components/modals/ModalListInvoiceFromPOS'
import ModalSendFromPOS from 'features/Invoice/components/modals/SendFromPOS'
import ModalSendInvoiceMail from 'features/Invoice/components/modals/SendInvoiceMail'
import {
  resetModals,
  setModalsState,
  setSelectedInvoice,
  setSelectedInvoices,
  useInvoiceSelector,
} from 'features/Invoice/invoiceSlice'
import ModalConvertPaper from 'features/Search/components/ModalConvertPaper'
import AppDnD from 'general/components/AppDragAndDrop/AppDnD'
import CustomAntButton from 'general/components/Button/CustomAntButton'
import ContentContainer from 'general/components/ContentContainer'
import CustomDataTable from 'general/components/CustomDataTable'
import FilterPopover from 'general/components/CustomPopover/FilterPopover'
import CustomAntInput from 'general/components/Forms/CustomAntInput/CustomAntInput'
import { IconAction } from 'general/components/GroupButton/styles'
import TableAction from 'general/components/GroupButton/TableAction'
import CancelInvoiceModal from 'general/components/Modal/CancelInvoiceModal'
import ExplanationInvoiceModal from 'general/components/Modal/ExplanationInvoiceModal'
import IssueInvoiceFromPosModal from 'general/components/Modal/IssueInvoiceFromPosModal'
import ModalConfirm from 'general/components/Modal/ModalConfirm'
import ModalSelectAdjustInvoiceMethod from 'general/components/Modal/ModalSelectAdjustInvoiceMethod'
import ModalSelectCancellationMethod from 'general/components/Modal/ModalSelectCancellationMethod'
import {
  INVOICE_STATUS,
  INVOICE_TYPE,
  SEND_TAX_STATUS,
} from 'general/constants/AppConstants'
import PreferenceKeys from 'general/constants/PreferenceKeys'
import SelectOptions from 'general/constants/SelectOptions'
import InvoiceHelper from 'general/helpers/InvoiceHelper'
import ToastHelper from 'general/helpers/ToastHelper'
import Utils, {
  findObjectInArray,
  getYearsFromDates,
} from 'general/utils/Utils'
import useGetSerials from 'hooks/Queries/useGetSerials'
import useQueryCompanyUnit from 'hooks/Queries/useQueryCompanyUnit'
import useAppDnD from 'hooks/useAppDnD'
import useFilter from 'hooks/useFilter'
import useFireSwal from 'hooks/useFireSwal'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import useRouter from 'hooks/useRouter'
import useSearchDebounce from 'hooks/useSearchDebounce'
import _ from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useMutationCancelInvoice,
  useMutationDownloadInvoice,
  useMutationPrintInvoice,
} from '../ListInvoice/queries'
import {
  listInvoiceFromPosQueries,
  useMutationBulkInvoicePaidStatus,
  useMutationDeleteInvoice,
  useMutationDownloadInvoice as useMutationDownloadInvoiceFromPos,
  useMutationInvoicePaidStatus,
  useMutationSendExplanationAnnounce,
  useMutationViewPdf,
} from './queries'
import ModalUploadInvoiceExcel from 'general/components/Modal/ModalUploadInvoiceExcel'
import { Spinner } from 'react-bootstrap'
import ModalSendEmail from 'general/components/Modal/ModalSendEmail'
import ModalSelectReplaceInvoiceMethod from 'general/components/Modal/ModalSelectReplaceInvoiceMethod'
import './style.scss'
import { useSearchParams } from 'react-router-dom'
import { selectFormatNumber } from 'features/System/screens/SystemConfiguration/SystemConfigurationSlice'
import ModalSocketInvoiceSign from 'general/components/Modal/ModalSocketInvoiceSign'
import { DownOutlined, PlusOutlined } from '@ant-design/icons'

export default function ListInvoiceFromPOS() {
  const [selectedInvoiceSendTax, setSelectedInvoiceSendTax] = useState([])
  const formatNumber = useAppSelector(selectFormatNumber)
  const { t } = useTranslation()
  const modalSentMailRef = useRef(null)
  const { navigate, query } = useRouter()
  const { debouncedSearch, search, setSearch } = useSearchDebounce('')
  const [filterParams, setFilterParams] = useFilter(
    'listInvoiceFromPOS',
    'gFilterListInvoiceFromPOS',
  )
  const dispatch = useAppDispatch()
  const { fireConfirm } = useFireSwal()
  const { activeSigningMethod } = useAppSelector(
    (state) => state.system.company,
  )
  const { modalStates, selectedInvoice, selectedInvoices, toggledClearRows } =
    useInvoiceSelector()
  const [goodsFilterParams] = useState({
    limit: 50,
    page: 0,
    status: 'ACTIVE',
    q: '',
  })
  const [dateSerial, setDateSerial] = useState([])
  const [isInvoiceAdjust, setIsInvoiceAdjust] = useState(false)
  const [hsmSignModalInfo, setHSMSignModalInfo] = useState({
    show: false,
    invoiceIds: [],
  })
  const [restoreOriginModalConfig, setRestoreOriginModalConfig] = useState({
    show: false,
    invoiceId: null,
  })

  const [searchParams] = useSearchParams()
  const sendTax = searchParams.get('sendTax')

  // ============ QUERIES ============
  const { data: serials } = useGetSerials('POS', undefined, dateSerial)
  const { mutate: handleViewInvoice } = useMutationViewPdf()
  const { mutate: updateInvoicePaidStatus, isPending: isUpdatingPaidStatus } =
    useMutationInvoicePaidStatus()
  const {
    mutate: updateBulkInvoicePaidStatus,
    isPending: isUpdatingBulkPaidStatus,
  } = useMutationBulkInvoicePaidStatus()

  const { mutate: handleDelete } = useMutationDeleteInvoice()
  const { mutate: downloadInvoices } = useMutationDownloadInvoice()

  const { handleDownloadPdf, handleDownloadXml } =
    useMutationDownloadInvoiceFromPos()

  const { mutate: printInvoices } = useMutationPrintInvoice()

  const { data: companyUnitList } = useQueryCompanyUnit({
    limit: 50,
    page: 1,
  })

  const { data: goodsOption } = useQuery({
    ...goodsQueries.listOfGoods(goodsFilterParams),
    select: (res) =>
      _.concat({ value: null, label: 'Tất cả hàng hóa' }, res?.goods).map(
        (v) => ({
          value: v.name ?? v.value,
          label: v.label ?? v.name,
        }),
      ),
  })
  const { mutate: sendExplanationAnnounceToCustomer } =
    useMutationSendExplanationAnnounce()

  const {
    data: listInvoiceData,
    isLoading: isGettingListData,
    isRefetching: isRefetchingListData,
    refetch: refetchListInvoice,
  } = useQuery(
    listInvoiceFromPosQueries.listInvoice({ ...filterParams, optimize: 1 }),
  )

  console.log('listInvoiceData', listInvoiceData)

  const { mutate: handleConvertToPaper } = useMutation({
    mutationKey: [listInvoiceFromPosQueries.base, { action: 'convertToPaper' }],
    mutationFn: (params) => invoiceApi.convertPaper(params),
    onSuccess: (res) => {
      if (!res) return
      ToastHelper.showSuccess('Chuyển thành hóa đơn giấy thành công')
      const fileURL = `${process.env.REACT_APP_BASE_URL}${res?.fileURL}`
      window.open(fileURL, '_blank')
    },
  })

  const { mutate: cancelInvoice } = useMutationCancelInvoice()

  const [findModalSocketInvoiceSign, setFindModalSocketInvoiceSign] = useState({
    show: false,
    objectId: null,
    createDate: null,
  })

  // ============ HANDLERS ============

  const issueInvoiceFromPosHandle = async ({ sendEmailHandle }) => {
    dispatch(setAppSpinning(false))
    try {
      let res = await invoiceApi.issueInvoicesMTT({
        invoiceIds: [selectedInvoice.invoiceId],
      })
      dispatch(setAppSpinning(false))

      if (res?.data) res = res.data
      if (
        !!res?.successIssueInvoices?.find(
          ({ invoiceId }) => invoiceId == selectedInvoice.invoiceId,
        )
      ) {
        ToastHelper.showSuccess('Phát hành hóa đơn thành công')

        //  sendEmail
        await sendEmailHandle()
        // reload page
        refetchListInvoice()
        return
      }
      // Phát hành lại thất bại
      ToastHelper.showError(res?.errorIssueInvoices?.at(0)?.reason)
      if (
        errorIssueInvoices?.at(0)?.reason !=
        'Ngày hóa đơn nhỏ hơn ngày hóa đơn được phát hành gần nhất. Vui lòng kiểm tra lại.'
      ) {
        refetchListInvoice()
      }
    } catch (e) {
      console.log(e)
      dispatch(setAppSpinning(false))
    }
  }

  const handleResendTaxInvoices = () => {
    if (!Array.isArray(selectedInvoices) || !selectedInvoices?.length) return

    let resendInvoiceIds = selectedInvoices
      .filter(({ sendTaxStatus }) => sendTaxStatus == 'SEND_ERROR')
      .map(({ invoiceId }) => invoiceId)

    if (!resendInvoiceIds.length) {
      ToastHelper.showError('Chưa có hóa đơn MTT để gửi lại CQT')
      return
    }

    if (activeSigningMethod == 'HSM') {
      setHSMSignModalInfo({ show: true, invoiceIds: resendInvoiceIds })
    } else {
      let accessToken = localStorage.getItem(PreferenceKeys.accessToken)
      let urlSchema = `${process.env.REACT_APP_BASE_URL_SCHEMA}${accessToken},summaryInvoiceSignedBySeller,`
      urlSchema =
        urlSchema +
        resendInvoiceIds
          ?.map((invoiceId, index, arr) =>
            index + 1 == arr.length ? invoiceId : invoiceId + ';',
          )
          ?.join('')

      Utils.openToolSignSummaryInvoice(urlSchema, accessToken)
    }
  }

  const handleHsmSignSummaryInvoice = async () => {
    dispatch(setAppSpinning(true))
    try {
      let invoiceIds = hsmSignModalInfo.invoiceIds
      await invoiceApi.hsmSignSummaryInvoice({ invoiceIds })
      dispatch(setAppSpinning(false))

      ToastHelper.showSuccess('Gửi dữ liệu MTT đến CQT thành công')
    } catch (e) {
      dispatch(setAppSpinning(false))
      console.log(e)
    } finally {
      refetchListInvoice()
    }
  }

  const restoreOrigin = async () => {
    dispatch(setAppSpinning(true))
    try {
      let res = await invoiceApi.restoreToOrigin(
        restoreOriginModalConfig.invoiceId,
      )
      dispatch(setAppSpinning(false))

      if (res?.result == 'success') {
        ToastHelper.showSuccess('Khôi phục hóa đơn thành công')
        // reload page
        refetchListInvoice()
        return
      }
    } catch (e) {
      console.log(e)
      dispatch(setAppSpinning(false))
    }
  }

  const fetchInvoiceData = async (invoiceId) => {
    try {
      const res = await invoiceApi.findById(invoiceId)
      if (res?.result === 'success') {
        handleDownloadXml({
          xmlString: res?.data?.xmlString,
          serial: res?.data?.serial,
          no: res?.data?.no,
        })
        // return res.data
      } else {
        ToastHelper.showError('Không tìm thấy dữ liệu')
      }
    } catch (err) {
      ToastHelper.showError('Có lỗi khi tìm dữ liệu')
    }
  }

  useEffect(() => {
    console.log('🚀 ~ ListInvoiceFromPOS ~ selectedInvoice:', selectedInvoice)
  }, [selectedInvoice])

  const renderSendEmail = (type) => {
    switch (type) {
      case 0:
        return <div className="badge badge-light-primary">Chưa gửi</div>
      case 1:
        return <div className="badge badge-light-success">Đã gửi</div>
      case 2:
        return <div className="badge badge-light-danger">Gửi thất bại</div>
      case 3:
        return <div className="badge badge-light-success">Gửi thành công</div>
      case 4:
        return <div className="badge badge-light-warning">Đang gửi</div>
      default:
        return <div className="badge badge-light-primary">Chưa gửi</div>
    }
  }

  const filterDefault = useMemo(() => {
    /** @type {FilterDefaultValueProp[]} */
    const arr = [
      {
        alias: 'Serial',
        label: 'Ký hiệu',
        formName: 'serials',
        value: null,
        options: serials,
        multiselect: true,
        colSpan: 'FULL',
      },
      {
        alias: 'Hóa đơn: Từ',
        label: ['Hóa đơn từ số', 'Đến số'],
        separator: 'đến',
        formName: ['fromNumberInvoice', 'toNumberInvoice'],
        value: [null, null],
        restrict: {
          type: 'number',
          min: 1,
          max: 99999999,
        },
      },
      {
        alias: 'Trạng thái HĐ',
        label: 'Trạng thái phát hành',
        formName: 'issueStatus',
        value: null,
        options: SelectOptions.issueStatus,
      },
      {
        alias: 'Loại HĐ',
        label: 'Loại hóa đơn',
        formName: 'types',
        value: null,
        options: SelectOptions.invoiceType,
      },
      {
        alias: 'Trạng thái gửi HĐ',
        label: 'Trạng thái gửi hóa đơn',
        formName: 'isSent',
        value: null,
        options: SelectOptions.isSent,
      },
      {
        alias: 'Trạng thái gửi CQT',
        label: 'Trạng thái gửi CQT',
        formName: 'sendTaxStatus',
        value: null,
        options: SelectOptions.sendTaxStatus,
      },
      {
        alias: 'Hàng hóa',
        label: 'Tên hàng hóa',
        formName: 'goodsName',
        options: goodsOption,
        value: null,
        colSpan: 'FULL',
      },
    ]
    return arr
  }, [goodsOption, serials])

  const columns = useMemo(() => {
    /** @type {import('react-data-table-component').TableColumn<InvoiceFromPos>[]} */
    const arr = [
      {
        id: 'companyUnitId',
        name: 'Đơn vị',
        center: true,
        hidden: true,
        selector: (row) =>
          companyUnitList?.find(
            (company) => company?.companyUnitId === row?.companyUnitId,
          )?.companyName ?? '',
        format: (row) => {
          const text =
            companyUnitList?.find(
              (company) => company?.companyUnitId === row?.companyUnitId,
            )?.companyName ?? ''
          return (
            <Tooltip destroyTooltipOnHide title={text} placement={'topLeft'}>
              <span>{text}</span>
            </Tooltip>
          )
        },
      },
      {
        id: 'serial',
        name: 'Ký hiệu',
        center: true,
        selector: (row) => row.serial,
      },
      {
        id: 'date',
        name: 'Ngày HĐ',
        center: true,
        format: (row) => dayjs(row.date).format('L'),
        selector: (row) => row.date,
      },
      {
        id: 'lookupCode',
        name: 'Mã tra cứu',
        center: true,
        hidden: true,
        selector: (row) => row.lookupCode,
      },
      {
        id: 'no',
        name: 'Số HĐ',
        center: true,
        selector: (row) => row.no,
      },
      {
        id: 'taxAuthorityCode',
        name: 'Mã CQT',
        center: true,
        minWidth: '150px',
        grow: 2,
        selector: (row) => row.taxAuthorityCode,
      },
      {
        id: 'customerCompanyName',
        name: 'Khách hàng',
        center: true,
        hidden: true,
        selector: (row) => row.customerCompanyName,
        format: (row) => (
          <Tooltip
            destroyTooltipOnHide
            title={row?.customerCompanyName}
            placement={'topLeft'}
          >
            <span>{row.customerCompanyName}</span>
          </Tooltip>
        ),
      },
      {
        id: 'customerTaxCode',
        name: 'Mã số thuế',
        hidden: true,
        center: true,
        minWidth: '150px',
        selector: (row) => row.customerTaxCode,
      },
      {
        id: 'customerFullAddress',
        name: 'Địa chỉ',
        hidden: true,
        center: true,
        selector: (row) => row.customerFullAddress,
        format: (row) => (
          <Tooltip
            destroyTooltipOnHide
            title={row?.customerFullAddress}
            placement={'topLeft'}
          >
            <span>{row.customerFullAddress}</span>
          </Tooltip>
        ),
      },
      {
        id: 'customerName',
        name: 'Người mua hàng',
        hidden: true,
        center: true,
        selector: (row) => row?.customerName || row?.customerCompanyName,
      },
      {
        id: 'customerEmail',
        name: 'Email',
        center: true,
        hidden: true,
        selector: (row) => row.customerEmail,
      },
      {
        id: 'customerPhone',
        name: 'SĐT',
        center: true,
        hidden: true,
        selector: (row) => row.customerPhone,
      },
      {
        id: 'customerPersonalIdentificationNumber',
        name: 'CCCD/CMND',
        center: true,
        hidden: true,
        selector: (row) => row.customerPersonalIdentificationNumber,
      },
      {
        id: 'customerAddress',
        name: 'Hình thức thanh toán',
        center: true,
        hidden: true,
        format: (row) => InvoiceHelper.renderPaymentMethod(row.paymentMethod),
        selector: (row) => row.paymentMethod,
      },
      {
        id: 'customerAccountNumber',
        name: 'TK ngân hàng',
        center: true,
        hidden: true,
        selector: (row) => row.customerAccountNumber,
      },
      {
        id: 'customerBankName',
        name: 'Tên ngân hàng',
        center: true,
        hidden: true,
        format: (row) => row.customerBankName,
      },
      {
        id: 'isPayment',
        name: 'Thanh toán',
        center: true,
        minWidth: '150px',
        allowOverflow: true,
        format: (row) => InvoiceHelper.renderIsPaid(row.isPayment),
        selector: (row) => row.isPayment,
      },
      {
        id: 'currency',
        name: 'Loại tiền',
        center: true,
        hidden: true,
        selector: (row) => row.currency,
      },
      {
        id: 'exchangeRate',
        name: 'Tỷ giá',
        center: true,
        style: {
          justifyContent: 'flex-end',
        },
        hidden: true,
        selector: (row) => row.exchangeRate,
        format: (row) =>
          Utils.formatNumber(row.exchangeRate, formatNumber?.exchangeRate),
      },
      {
        id: 'totalAfterTax',
        name: 'Tổng tiền',
        center: true,
        style: {
          justifyContent: 'flex-end',
        },
        hidden: true,
        minWidth: '170px',
        format: (row) =>
          Utils.formatNumber(
            row.totalAfterTax,
            ['VNĐ', 'VND'].includes(row.currency)
              ? (row?.numberFormat ?? formatNumber)?.currencyExchange
              : (row?.numberFormat ?? formatNumber)?.foreignCurrency,
          ),
        selector: (row) => row.totalAfterTax,
      },
      {
        id: 'type',
        name: 'Trạng thái HĐ',
        center: true,
        minWidth: '150px',
        format: (row) => {
          const { color, text } = InvoiceHelper.renderInvoiceType(row?.type)
          return <div className={`badge badge-light-${color}`}>{text}</div>
        },
        selector: (row) => row.type,
      },
      {
        id: 'issueStatus',
        name: 'Trạng thái phát hành',
        minWidth: '150px',
        center: true,
        format: (row) => {
          const { color, text } = InvoiceHelper.renderIssueStatus(
            row.issueStatus,
          )
          return <div className={`badge badge-light-${color}`}>{text}</div>
        },
        selector: (row) => row.issueStatus,
      },
      {
        id: 'sendTaxStatus',
        name: 'Trạng thái gửi CQT',
        minWidth: '150px',
        allowOverflow: true,
        center: true,
        format: (row) => {
          const { color, text } = InvoiceHelper.renderSendTaxStatus(
            row.sendTaxStatus,
          )
          return <div className={`badge badge-light-${color}`}>{text}</div>
        },
        selector: (row) => row.sendTaxStatus,
      },
      {
        id: 'isSent',
        name: 'Gửi mail',
        center: true,
        // cell: (row) => (
        //   <IconAction
        //     className={Utils.cn(
        //       'fa-duotone',
        //       row.isSent
        //         ? 'fa-circle-check'
        //         : 'fa-do-not-enter text-light-dark',
        //     )}
        //     style={
        //       row.isSent
        //         ? {
        //             '--fa-primary-color': '#fff',
        //             '--fa-secondary-color': '#50cd89',
        //             '--fa-secondary-opacity': 1,
        //           }
        //         : {}
        //     }
        //   />
        // ),
        cell: (row) => {
          return (
            <p
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-normal m-0 text-maxline-3 "
            >
              {renderSendEmail(row?.isSent)}
            </p>
          )
        },
      },
      {
        id: 'customerSignName',
        name: 'Người mua ký',
        center: true,
        hidden: true,

        selector: (row) => row.customerName,
      },
      {
        id: 'licensePlate',
        name: 'Biển số xe',
        center: true,
        hidden: process.env.REACT_APP_BASE_URL == 'PETRO',
        cell: (row) => {
          return (
            <p
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-normal m-0 text-maxline-3 "
            >
              {row.licensePlate
                ? row.licensePlate
                : listInvoiceData?.dataInvoiceLicensePlate[row?.invoiceId]}
            </p>
          )
        },
        // selector: (row) =>
        //   row.licensePlate ??
        //   listInvoiceData?.dataInvoiceLicensePlate[row?.invoiceId],
      },
      {
        id: 'customerCode',
        name: 'Mã khách hàng',
        center: true,
        hidden: true,

        selector: (row) => row.customerCode,
      },
      {
        id: 'arrivalDate',
        name: 'Ngày đến',
        center: true,
        hidden: true,

        selector: (row) => row.arrivalDate,
      },
      {
        id: 'invoiceCode',
        name: 'Mã hóa đơn',
        center: true,
        hidden: true,
        minWidth: '150px',
        cell: (row) => {
          return (
            <p
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-normal m-0 text-maxline-3 "
            >
              <Tooltip placement="topLeft" title={row.invoiceCode}>
                {row.invoiceCode}
              </Tooltip>
            </p>
          )
        },
        selector: (row) => row.invoiceCode,
      },
      {
        name: 'Hành động',
        disabled: true,
        center: true,
        compact: true,
        style: {
          justifyContent: 'flex-end',
        },
        grow: 1.75,
        minWidth: '200px',
        cell: (row) => {
          return (
            <div>
              <TableAction
                titleActionText="Xem"
                icon={<IconAction className="fa-light fa-eye" />}
                onClick={() => {
                  handleViewInvoice(row.invoiceId)
                }}
              />

              {/* Phát hành hóa đơn */}
              {[
                INVOICE_STATUS.NOT_SENT_TO_AUTHORITY,
                INVOICE_STATUS.SEND_ERROR,
                INVOICE_STATUS.SIGN_ERROR,
              ].includes(row.issueStatus) &&
              row.type !== INVOICE_TYPE.DELETED ? (
                <TableAction
                  titleActionText={'Phát hành hoá đơn'}
                  icon={<IconAction className="fa-light fa-paper-plane" />}
                  onClick={() => {
                    dispatch(setSelectedInvoice(row))
                    dispatch(
                      setModalsState({
                        modalName: 'issueInvoiceFromPos',
                        status: true,
                      }),
                    )
                  }}
                />
              ) : null}

              {/* Cập nhật hóa đơn */}
              {[INVOICE_STATUS.NOT_SENT_TO_AUTHORITY].includes(
                row.issueStatus,
              ) && row.type !== INVOICE_TYPE.DELETED ? (
                <TableAction
                  titleActionText={t('Edit')}
                  icon={<IconAction className="fa-light fa-pen-to-square" />}
                  onClick={() => {
                    window.location.href = `/hoa-don-dau-ra/hoa-don/may-tinh-tien/cap-nhat/${row?.invoiceId}`
                    // navigate(
                    //   `/hoa-don-dau-ra/hoa-don/may-tinh-tien/cap-nhat/${row?.invoiceId}`,
                    // )
                  }}
                />
              ) : null}

              {/* Xoá hóa đơn */}
              {[
                INVOICE_STATUS.NOT_SENT_TO_AUTHORITY,
                INVOICE_STATUS.SIGN_ERROR,
                INVOICE_STATUS.SEND_ERROR,
              ].includes(row.issueStatus) &&
              row.type !== INVOICE_TYPE.DELETED ? (
                <TableAction
                  titleActionText="Xoá"
                  icon={<IconAction className="fa-light fa-trash" />}
                  onClick={async () => {
                    const result = await fireConfirm(
                      'Xác nhận xóa hóa đơn',
                      'Bạn có chắc chắn muốn xóa hóa đơn này?',
                    )
                    if (!result.value) return false
                    handleDelete([row?.invoiceId])
                  }}
                />
              ) : null}

              {/* Gửi hóa đơn cho khách hàng */}
              <TableAction
                titleActionText="Gửi hóa đơn cho KH"
                icon={<IconAction className="fa-light fa-envelope" />}
                onClick={() => {
                  dispatch(setSelectedInvoice(row))
                  dispatch(
                    setModalsState({
                      modalName: 'sendDraftInvoice',
                      status: true,
                    }),
                  )
                }}
              />

              {/* More actions */}
              <TableAction
                type="dropdown"
                items={[
                  row?.issueStatus === INVOICE_STATUS.AUTHORITY_CODE_GIVEN &&
                    row?.type !== INVOICE_TYPE.DELETED && {
                      label: 'Tải hóa đơn PDF',
                      icon: (
                        <IconAction className="fa-light fa-file-download" />
                      ),
                      onClick: () =>
                        handleDownloadPdf({
                          invoiceId: row.invoiceId,
                          serial: row.serial,
                          no: row.no,
                        }),
                    },
                  row?.issueStatus === INVOICE_STATUS.AUTHORITY_CODE_GIVEN &&
                    row?.type !== INVOICE_TYPE.DELETED && {
                      label: 'Tải hóa đơn XML',
                      icon: <IconAction className="fa-light fa-file-code" />,
                      onClick: () => fetchInvoiceData(row?.invoiceId),
                    },
                  // Nhân bản hóa đơn
                  {
                    label: 'Nhân bản hóa đơn',
                    icon: <IconAction className="fa-light fa-clone" />,
                    onClick: () => {
                      navigate(
                        `/hoa-don-dau-ra/hoa-don/may-tinh-tien/them-moi/${row?.invoiceId}`,
                      )
                    },
                  },
                  // Gửi thông báo giải trinh
                  row?.issueStatus === INVOICE_STATUS.AUTHORITY_CODE_GIVEN &&
                    row?.type === 0 && {
                      label: 'Gửi thông báo giải trình cho KH',
                      icon: <IconAction className="fa-light fa-envelope" />,
                      onClick: () => {
                        if (![0, 4].includes(row?.errAnnouceType)) {
                          let describeText = `Hóa đơn <${row.serial} - ${
                            row.no
                          }> đã được thông báo sai sót với cơ quan thuế và chọn hình thức xử lý là <${InvoiceHelper.errAnnouceTypeToVnese(
                            row.errAnnouceType,
                          )}>. Bạn không được phép gửi thông báo giải trình sai sót cho hóa đơn này.`
                          dispatch(showNotification({ describeText }))
                          return
                        }

                        // Trường hợp chưa lập tbss
                        if (row.errAnnouceType == 0) {
                          // đã lập hóa đơn thay thế, điều chỉnh
                          if (row.Related?.at(0)) {
                            let relatedType = row.Related.at(0).type
                            if (![1, 2].includes(relatedType)) {
                              ToastHelper.showError(
                                'Hoá đơn thay thế hoặc điều chỉnh cho hoá đơn này đã xảy ra lỗi trạng thái. Cần thông báo cho nhà cung cấp kiểm tra',
                              )
                              return
                            }

                            let relatedTypeVNese =
                              relatedType === 1 ? 'Thay thế' : 'Điều chỉnh'
                            let describeText = `Hóa đơn <${row.serial} - ${
                              row.no
                            }> đã được lập hóa đơn ${relatedTypeVNese} số <${
                              row.Related.serial
                            } - ${
                              row.Related.no ?? 'Chưa câp số'
                            }>. Nếu tiếp tục phát hiện ra sai sót, bạn cần thực hiện theo hình thức đã áp dụng khi xử lý sai sót lần đầu (${relatedTypeVNese}).`
                            dispatch(showNotification({ describeText }))
                            return
                          }

                          // kiểm tra xem có lập biên bản hủy hay điều chỉnh không
                          if (row.reportType) {
                            let reportTypeVnese =
                              row.reportType == 'CANCEL' ? 'hủy' : 'điều chỉnh'
                            let describeText = `Hóa đơn <${row.serial} - ${row.no}> đã được lập biên bản ${reportTypeVnese}. Nếu tiếp tục phát hiện ra sai sót, bạn cần thực hiện theo hình thức đã áp dụng khi xử lý sai sót lần đầu (${reportTypeVnese}).`
                            dispatch(showNotification({ describeText }))
                            return
                          }
                        }
                        dispatch(setSelectedInvoice(row))
                        dispatch(
                          setModalsState({
                            modalName: 'explanation',
                            status: true,
                          }),
                        )
                      },
                    },
                  {
                    type: 'divider',
                  },
                  (row?.type === 0 || row?.type === 5) &&
                    row?.issueStatus ===
                      INVOICE_STATUS.AUTHORITY_CODE_GIVEN && {
                      label: 'Điều chỉnh hoá đơn',
                      icon: (
                        <IconAction className="fa-light fa-pen-to-square" />
                      ),
                      onClick: () => {
                        // if (isCreateAdjustedInvoice) {
                        if (![0, 2, 4].includes(row.errAnnouceType)) {
                          let describeText
                          const errAnnouceTypeText =
                            InvoiceHelper.errAnnouceTypeToVnese(
                              row.errAnnouceType,
                            )
                          if ([5].includes(row?.invoiceErrAnnouceStatus)) {
                            // if ([3, 5].includes(row?.invoiceErrAnnouceStatus)) {
                            describeText = `Hóa đơn <${row.serial} - ${row.no}> đã được thông báo sai sót với cơ quan thuế và chọn hình thức xử lý là ${errAnnouceTypeText}. Bạn không được phép điều chỉnh hóa đơn này.`
                            dispatch(showNotification({ describeText }))
                            return
                          }
                          // if ([2, 6].includes(row?.invoiceErrAnnouceStatus)) {
                          if ([6].includes(row?.invoiceErrAnnouceStatus)) {
                            describeText = `Hóa đơn <${row.serial} - ${row.no}> đã lập thông báo sai sót với hình thức xử lý là ${errAnnouceTypeText} nhưng chưa gửi CQT. Bạn cần hoàn thành TBSS này.`
                            dispatch(showNotification({ describeText }))
                            return
                          }
                        }
                        // đã lập hóa đơn thay thế, điều chỉnh
                        if (
                          row.Related?.at(0) &&
                          row.Related?.at(0).sendTaxStatus !=
                            SEND_TAX_STATUS.INVOICE_INVALID
                        ) {
                          let relatedType = row.Related.at(0).type
                          if (![1, 2].includes(relatedType)) {
                            ToastHelper.showError(
                              'Hoá đơn thay thế hoặc điều chỉnh cho hoá đơn này đã xảy ra lỗi trạng thái. Cần thông báo cho nhà cung cấp kiểm tra',
                            )
                            return
                          }

                          if (relatedType == 1) {
                            let describeText = `Hóa đơn <${row.serial} - ${
                              row.no
                            }> đã được lập hóa đơn Thay thế số <${
                              row.Related.serial
                            } - ${
                              row.Related.no ?? 'Chưa cấp số'
                            }> . Nếu tiếp tục phát hiện ra sai sót, bạn cần thực hiện theo hình thức đã áp dụng khi xử lý sai sót lần đầu (Thay thế).`
                            dispatch(showNotification({ describeText }))
                            return
                          }
                          // show modal confirm
                          dispatch(setSelectedInvoice(row))
                          setIsInvoiceAdjust(true)
                          return
                        }

                        // Trường hợp chưa lập tbss
                        if (row.errAnnouceType == 0) {
                          // kiểm tra xem có lập biên bản hủy không
                          if (row.reportType == 'CANCEL') {
                            let describeText = `Hóa đơn <${row.serial} - ${row.no}> đã được lập biên bản hủy. Nếu tiếp tục phát hiện ra sai sót, bạn cần thực hiện theo hình thức đã áp dụng khi xử lý sai sót lần đầu (hủy).`
                            dispatch(showNotification({ describeText }))
                            return
                          }
                        }
                        dispatch(setSelectedInvoice(row))
                        dispatch(
                          setModalsState({
                            modalName: 'selectAdjustInvoiceMethod',
                            status: true,
                          }),
                        )
                        // }
                        // if (!isCreateAdjustedInvoice)
                        //   dispatch(
                        //     setModalsState({
                        //       modalName: 'noRight',
                        //       status: true,
                        //     }),
                        //   )
                      },
                    },
                  row?.issueStatus === INVOICE_STATUS.AUTHORITY_CODE_GIVEN &&
                    (row?.type === 0 ||
                      row?.type === 1 ||
                      row?.type === 3 ||
                      row?.type === 4) && {
                      label: 'Thay thế hoá đơn',
                      icon: <IconAction className="fa-light fa-file-minus" />,
                      onClick: () => {
                        // if (isCreateReplacementInvoice) {
                        // đã lập thông báo huỷ, điều chỉnh
                        if (![0, 3, 4].includes(row.errAnnouceType)) {
                          let describeText
                          // if ([3, 5].includes(row?.invoiceErrAnnouceStatus)) {
                          //   describeText = `Hóa đơn <${row.serial} - ${
                          //     row.no
                          //   }> đã được thông báo sai sót với cơ quan thuế và chọn hình thức xử lý là ${InvoiceHelper.errAnnouceTypeToVnese(
                          //     row.errAnnouceType,
                          //   )}. Bạn không được phép thay thế hóa đơn này.`
                          //   dispatch(showNotification({ describeText }))
                          //   return
                          // }
                          // if ([2, 6].includes(row?.invoiceErrAnnouceStatus)) {
                          if ([6].includes(row?.invoiceErrAnnouceStatus)) {
                            describeText = `Hóa đơn <${row.serial} - ${
                              row.no
                            }> đã lập thông báo sai sót với hình thức xử lý là ${InvoiceHelper.errAnnouceTypeToVnese(
                              row.errAnnouceType,
                            )} nhưng chưa gửi CQT. Bạn cần hoàn thành TBSS này.`
                            dispatch(showNotification({ describeText }))
                            return
                          }
                        }
                        if (row.type === INVOICE_TYPE.CANCELLED) {
                          navigate(
                            `/hoa-don-dau-ra/xu-ly-hoa-don/may-tinh-tien/thay-the/${row?.invoiceId}`,
                          )
                          return
                        }
                        if (row.type === INVOICE_TYPE.REPLACED) {
                          if (row.Related?.at(0)) {
                            let describeText = `Hóa đơn <${row.serial} - ${
                              row.no
                            }> đã được lập hóa đơn Thay thế số <${
                              row.Related.at(0).serial
                            } - ${
                              row.Related.at(0).no ?? 'Chưa câp số'
                            }>. Bạn không thể tiếp tục lập hoá đơn thay thế cho hoá đơn này.`
                            dispatch(showNotification({ describeText }))
                            return
                          }
                          navigate(
                            `/hoa-don-dau-ra/xu-ly-hoa-don/may-tinh-tien/thay-the/${row?.invoiceId}`,
                          )
                          return
                        }
                        // Trường hợp chưa lập tbss
                        if (row.errAnnouceType == 0) {
                          // đã lập hóa đơn thay thế, điều chỉnh
                          if (
                            row.Related?.at(0) &&
                            row.Related?.at(0).sendTaxStatus !=
                              SEND_TAX_STATUS.INVOICE_INVALID
                          ) {
                            let relatedType = row.Related.at(0).type
                            if (![1, 2].includes(relatedType)) {
                              ToastHelper.showError(
                                'Hoá đơn thay thế hoặc điều chỉnh cho hoá đơn này đã xảy ra lỗi trạng thái. Cần thông báo cho nhà cung cấp kiểm tra',
                              )
                              return
                            }

                            let relatedTypeVNese =
                              relatedType === 1 ? 'Thay thế' : 'Điều chỉnh'
                            let describeText = `Hóa đơn <${row.serial} - ${
                              row.no
                            }> đã được lập hóa đơn ${relatedTypeVNese} số <${
                              row.Related.at(0).serial
                            } - ${
                              row.Related.at(0).no ?? 'Chưa câp số'
                            }>. Nếu tiếp tục phát hiện ra sai sót, bạn cần thực hiện theo hình thức đã áp dụng khi xử lý sai sót lần đầu (${relatedTypeVNese}).`
                            dispatch(showNotification({ describeText }))
                            return
                          }

                          // kiểm tra xem có lập biên bản hủy không
                          if (row.reportType == 'CANCEL') {
                            let describeText = `Hóa đơn <${row.serial} - ${row.no}> đã được lập biên bản hủy. Nếu tiếp tục phát hiện ra sai sót, bạn cần thực hiện theo hình thức đã áp dụng khi xử lý sai sót lần đầu (hủy).`
                            dispatch(showNotification({ describeText }))
                            return
                          }
                        }
                        dispatch(setSelectedInvoice(row))
                        dispatch(
                          setModalsState({
                            modalName: 'selectReplacementMethod',
                            status: true,
                          }),
                        )
                        // }
                        // if (!isCreateReplacementInvoice)
                        //   dispatch(
                        //     setModalsState({
                        //       modalName: 'noRight',
                        //       status: true,
                        //     }),
                        //   )
                      },
                    },

                  row?.issueStatus == INVOICE_STATUS.AUTHORITY_CODE_GIVEN &&
                    row?.type == 3 && {
                      label: 'Khôi phục về HĐ gốc',
                      icon: <IconAction className="fa-light fa-rotate-left" />,
                      onClick: () => {
                        setRestoreOriginModalConfig({
                          show: true,
                          invoiceId: row?.invoiceId,
                        })
                      },
                    },
                  {
                    type: 'divider',
                  },
                  /* : null */ // Thay đổi trạng thái thanh toán
                  row?.issueStatus === INVOICE_STATUS.AUTHORITY_CODE_GIVEN &&
                    row?.type !== INVOICE_TYPE.DELETED && {
                      label: 'Đã thanh toán',
                      disabled: isUpdatingPaidStatus,
                      icon: isUpdatingPaidStatus ? (
                        <Spinner animation="grow" size="sm" />
                      ) : (
                        <IconAction
                          className={Utils.cn(
                            row.isPayment
                              ? 'fa-light fa-check text-success'
                              : 'fa-light fa-square',
                          )}
                        />
                      ),
                      onClick: () => {
                        updateInvoicePaidStatus({
                          invoiceId: row.invoiceId,
                          isPaid: !row.isPayment,
                        })
                      },
                    },
                  // Chuyển sang hóa đơn giấy
                  [
                    INVOICE_STATUS.AUTHORITY_ACCEPT,
                    INVOICE_STATUS.AUTHORITY_DENY,
                    INVOICE_STATUS.AUTHORITY_CODE_GIVEN,
                    INVOICE_STATUS.AUTHORITY_CODE_NOT_GIVEN,
                  ].includes(row?.issueStatus)
                    ? {
                        label: 'Chuyển thành hóa đơn giấy',
                        icon: (
                          <IconAction className="fa-light fa-paper-plane-alt" />
                        ),
                        onClick: () => {
                          dispatch(setSelectedInvoice(row))
                          dispatch(
                            setModalsState({
                              modalName: 'convertPaperModal',
                              status: true,
                            }),
                          )
                        },
                      }
                    : null,
                ]}
              />
            </div>
          )
        },
      },
    ]
    return arr
  }, [
    companyUnitList,
    dispatch,
    fireConfirm,
    handleDelete,
    handleDownloadPdf,
    handleDownloadXml,
    handleViewInvoice,
    isUpdatingPaidStatus,
    navigate,
    t,
    updateInvoicePaidStatus,
    listInvoiceData?.dataInvoiceLicensePlate,
    formatNumber,
  ])

  const dynamicColumns = useAppDnD(columns)
  const invoiceSelectedActions = [
    {
      key: 'issue',
      icon: 'fa-light fa-paper-plane',
      text: `Phát hành (${selectedInvoices.length})`,
      type: 'primary',
      disabled: selectedInvoices.some(
        (invoice) =>
          invoice.issueStatus !== INVOICE_STATUS.NOT_SENT_TO_AUTHORITY &&
          invoice.issueStatus !== INVOICE_STATUS.SIGN_ERROR,
      ),
      onClick: () =>
        dispatch(
          setModalsState({ modalName: 'issueInvoicesFromPos', status: true }),
        ),
    },
    {
      key: 'send',
      icon: 'fa-light fa-envelope',
      text: `Gửi (${selectedInvoices.length})`,
      type: 'primary',
      color: '#0891B2',
      disabled: false,
      onClick: () => {
        const customer_arr = _.map(selectedInvoices, 'customerTaxCode').filter(
          (code) => !_.isEmpty(code),
        )
        const hasMultipleCode = _.uniq(customer_arr).length > 1
        if (hasMultipleCode) {
          dispatch(
            setModalsState({
              modalName: 'detectMultipleCustomer',
              status: true,
            }),
          )
          return
        } else {
          dispatch(
            setModalsState({
              modalName: 'sentMail',
              status: true,
            }),
          )
        }
      },
    },
    {
      key: 'print',
      icon: 'fa-light fa-print',
      text: `In (${selectedInvoices.length})`,
      type: 'primary',
      color: '#4F46E5',
      disabled: false,
      onClick: () =>
        printInvoices(selectedInvoices.map((invoice) => invoice.invoiceId)),
    },
    {
      key: 'paid',
      icon: 'fa-regular fa-check',
      text: 'Đã thanh toán',
      variant: 'success',
      type: 'primary',
      isLoading: isUpdatingBulkPaidStatus,
      onClick: () => {
        updateBulkInvoicePaidStatus({
          invoiceIds: selectedInvoices
            .filter((invoice) => !invoice.isPayment)
            .map((invoice) => invoice.invoiceId),
          isPayment: true,
        })
      },
    },
    {
      key: 'delete',
      icon: 'fa-regular fa-trash',
      text: `Xoá ${selectedInvoices.length} hóa đơn`,
      type: 'primary',
      danger: true,
      disabled: selectedInvoices.some((invoice) =>
        [SEND_TAX_STATUS.INVOICE_VALID].includes(invoice.sendTaxStatus),
      ),
      onClick: async () => {
        const result = await fireConfirm(
          `Xác nhận xóa ${selectedInvoices.length} hóa đơn`,
          'Bạn chắc chắn muốn xóa các hóa đơn này?',
        )
        if (!result.value) return
        handleDelete(selectedInvoices.map((item) => item.invoiceId))
      },
    },
    {
      key: 'resend',
      icon: 'fa-regular fa-paper-plane',
      text: `Gửi lại ${selectedInvoices.length} hóa đơn lên CQT`,
      type: 'primary',
      disabled: !selectedInvoices.some(
        ({ sendTaxStatus }) => sendTaxStatus === 'SEND_ERROR',
      ),
      onClick: async () => {
        const result = await fireConfirm(
          'Xác nhận gửi lại hóa đơn lên CQT',
          'Chương trình chỉ thực hiện gửi lại các hóa đơn đã cấp số, có trạng thái gửi CQT là Gửi lỗi',
        )
        if (result.isDismissed) return
        handleResendTaxInvoices(selectedInvoices)
      },
    },
    {
      key: 'download',
      icon: 'fa-regular fa-file-download',
      text: `Tải về ${selectedInvoices.length}`,
      type: 'primary',
      disabled: !selectedInvoices.some(
        (invoice) =>
          [
            INVOICE_STATUS.AUTHORITY_CODE_GIVEN,
            INVOICE_STATUS.AUTHORITY_ACCEPT,
          ].includes(invoice.issueStatus) &&
          ![
            INVOICE_TYPE.CANCELLED,
            INVOICE_TYPE.REPLACED,
            INVOICE_TYPE.DELETED,
          ].includes(invoice.type),
      ),
      onClick: () => downloadInvoices({ invoices: selectedInvoices }),
    },
  ]

  useEffect(() => {
    setFilterParams({ q: debouncedSearch })
  }, [debouncedSearch, setFilterParams])

  useEffect(() => {
    dispatch(resetModals())
  }, [dispatch])

  const onchangeDate = (name, value, dateRange) => {
    if (name) {
      setDateSerial(getYearsFromDates({ ...filterParams, [name]: value }))
    } else {
      setDateSerial(
        getYearsFromDates({
          ...filterParams,
          startDate: dateRange[0],
          endDate: dateRange[1],
        }),
      )
    }
  }

  useEffect(() => {
    setDateSerial(getYearsFromDates(filterParams))
  }, [])

  const NotificationButton = ({ count = 0, children }) => {
    if (!count) return children

    return (
      <div className="fb-btn-wrapper">
        {children}
        <span className="fb-badge">{count > 99 ? '99+' : count}</span>
      </div>
    )
  }
  useEffect(() => {
    if (sendTax === '1') {
      dispatch(
        setModalsState({
          modalName: 'sentFromPos',
          status: true,
        }),
      )
      navigate(location.pathname, { replace: true })
    }
  }, [sendTax])

  const { data: queryData, refetch } = useQuery(
    listInvoiceFromPosQueries.listInvoiceMttNeedSendTax({
      ...filterParams,
      viewSendCQT: 1,
      notify: 1,
    }),
  )

  useEffect(() => {
    if (!modalStates.sentFromPos) {
      refetch()
    }
  }, [modalStates.sentFromPos])

  const handleMenuClick = (e) => {
    switch (e.key) {
      case 'chiet-khau-thuong-mai':
        navigate(
          '/hoa-don-dau-ra/hoa-don/may-tinh-tien/chiet-khau-thuong-mai/them-moi',
        )
        break
      default:
        break
    }
  }

  const items = [
    {
      label: 'Lập hóa đơn chiết khấu thương mại',
      key: 'chiet-khau-thuong-mai',
      icon: <PlusOutlined />,
    },
  ]
  const menuProps = {
    items,
    onClick: handleMenuClick,
  }

  return (
    <ContentContainer>
      <ContentContainer.Header
        titleContent="HĐ khởi tạo từ máy tính tiền"
        description={
          <div className="d-flex justify-content-center align-items-center">
            <i className="fa-solid fa-message-question text-primary fa-2x mr-1" />
            <span className="fw-medium fs-6 fst-italic">
              Hóa đơn khởi tạo từ máy tính tiền cần gửi đến CQT trong ngày
            </span>
          </div>
        }
        toolBar={
          <Space size={3}>
            <Space.Compact>
              <CustomAntButton
                text="Tạo hoá đơn"
                iconCn="fa-regular fa-plus text-white"
                borderRadius="5px 0px 0px 5px"
                antProps={{
                  type: 'primary',
                  onClick: () =>
                    navigate({
                      pathname:
                        '/hoa-don-dau-ra/hoa-don/may-tinh-tien/them-moi',
                    }),
                }}
              />

              <Dropdown
                menu={menuProps}
                placement="bottomRight"
                trigger={['click']} // hoặc ['hover']
              >
                <Button
                  type="primary"
                  icon={<DownOutlined />}
                  style={{
                    padding: '8px 12px',
                    minWidth: 40,
                    height: 'auto',
                    borderLeft: '1px solid rgba(255,255,255,0.3)',
                  }}
                />
              </Dropdown>
            </Space.Compact>

            <CustomAntButton
              text="Nhập khẩu"
              iconCn="far fa-file-import"
              antProps={{
                type: 'default',
                onClick: () =>
                  dispatch(
                    setModalsState({
                      modalName: 'upload',
                      status: true,
                    }),
                  ),
              }}
            />
            <NotificationButton count={queryData?.invoices?.length}>
              <CustomAntButton
                text={'Gửi dữ liệu MTT đến CQT'}
                iconCn="far fa-file-export"
                antProps={{
                  type: 'default',
                  onClick: () => {
                    dispatch(
                      setModalsState({
                        modalName: 'sentFromPos',
                        status: true,
                      }),
                    )
                  },
                }}
              />
            </NotificationButton>
          </Space>
        }
      >
        <Flex className="w-100" justify={'space-between'}>
          {selectedInvoices.length > 0 ? (
            <>
              <Flex className="w-100" gap={10}>
                {invoiceSelectedActions.map(
                  ({ key, icon, text, variant, color, ...antProps }) => (
                    <CustomAntButton
                      key={key}
                      variant={variant}
                      iconCn={icon}
                      color={color}
                      text={text}
                      antProps={antProps}
                    />
                  ),
                )}
              </Flex>
            </>
          ) : (
            <Space
              wrap
              className={Utils.cn(selectedInvoices.length > 0 ? 'd-none' : '')}
            >
              <CustomAntInput
                type="search"
                borderRadius="sm"
                width={250}
                inputProps={{
                  value: search,
                  onChange: (e) => setSearch(e.target.value),
                }}
              />
              <FilterPopover
                defaultValues={filterDefault}
                savedKey="gFilterListInvoiceFromPOS"
                onApply={setFilterParams}
                onchangeDate={onchangeDate}
              />
            </Space>
          )}
          <div>
            <AppDnD
              defaultColumns={columns}
              localStorageName="invoiceFromPos"
            />
          </div>
        </Flex>
      </ContentContainer.Header>
      <ContentContainer.Body>
        <CustomDataTable
          columns={dynamicColumns}
          dataSource={listInvoiceData?.tableData}
          isLoading={isGettingListData || isRefetchingListData}
          isSelectable
          isClickable
          persistTableHead
          handleDoubleClickRow={(row) => handleViewInvoice(row.invoiceId)}
          selectedRows={{
            clearSelectedRows: toggledClearRows,
            selectableRowSelected: (row) => {
              _.includes(selectedInvoices, row)
            },
            handleSelectedRows: ({ selectedRows }) => {
              dispatch(setSelectedInvoices(selectedRows))
            },
          }}
          pagination={{
            currentPage: filterParams.page,
            rowsPerPage: filterParams.limit,
            total: listInvoiceData?.total,
            onChangePage: (page) => setFilterParams({ page: parseInt(page) }),
            onChangeRowsPerPage: (limit) =>
              setFilterParams({ page: 1, limit: parseInt(limit) }),
          }}
        />
      </ContentContainer.Body>
      <ModalSendFromPOS
        show={modalStates.sentFromPos}
        onOpenChange={(status) => {
          dispatch(
            setModalsState({
              modalName: 'sentFromPos',
              status,
            }),
          )
        }}
        invoiceType="INVOICE"
        refetchListInvoice={refetchListInvoice}
        selectedInvoiceSendTax={selectedInvoiceSendTax}
        setSelectedInvoiceSendTax={setSelectedInvoiceSendTax}
      />
      <ModalListInvoiceFromPOS
        show={modalStates.listInvoiceFromPOS}
        onOpenChange={(status) => {
          dispatch(
            setModalsState({
              modalName: 'listInvoiceFromPOS',
              status,
            }),
          )
          dispatch(
            setModalsState({
              modalName: 'sentFromPos',
              status: true,
            }),
          )
        }}
        selectedInvoiceSendTax={selectedInvoiceSendTax}
        setSelectedInvoiceSendTax={setSelectedInvoiceSendTax}
      />
      <IssueInvoicesFromPosModal
        show={modalStates.issueInvoicesFromPos}
        onOpenChange={(status) =>
          dispatch(
            setModalsState({
              modalName: 'issueInvoicesFromPos',
              status,
            }),
          )
        }
        refetchListInvoice={refetchListInvoice}
      />
      <IssueInvoiceFromPosModal
        invoice={selectedInvoice}
        onIssueSuccess={refetchListInvoice}
        show={modalStates.issueInvoiceFromPos}
        onHide={() => {
          dispatch(
            setModalsState({
              modalName: 'issueInvoiceFromPos',
              status: false,
            }),
          )
          dispatch(setSelectedInvoice(null))
        }}
      />
      <ModalSendInvoiceMail
        onHandleApply={() => {
          dispatch(setSelectedInvoice(null))
          dispatch(setSelectedInvoices([]))
          refetchListInvoice()
        }}
      />
      <ExplanationInvoiceModal
        show={modalStates.explanation}
        onHide={() =>
          dispatch(
            setModalsState({
              modalName: 'explanation',
              status: false,
            }),
          )
        }
        title="Gửi thông báo giải trình cho khách hàng"
        invoice={selectedInvoice}
        buttonAgreeOnClick={(values) => {
          sendExplanationAnnounceToCustomer(values)
          dispatch(
            setModalsState({
              modalName: 'sendExplanationAnnounce',
              status: false,
            }),
          )
        }}
      />
      <ModalConvertPaper
        showModalConvertPaper={modalStates.convertPaperModal}
        setShowModalConvertPaper={(status) => {
          dispatch(
            setModalsState({
              modalName: 'convertPaperModal',
              status,
            }),
          )
        }}
        handleConvertPaper={(converterName) =>
          handleConvertToPaper({
            lookupCode: selectedInvoice.lookupCode,
            converterName,
          })
        }
      />
      {/* <ModalSelectCancellationMethod
        show={modalStates.selectCancelationMethod}
        setShow={(status) =>
          dispatch(
            setModalsState({
              modalName: 'selectCancelationMethod',
              status,
            }),
          )
        }
        header="Xác nhận"
        content="Bạn có thể lập thông báo hóa đơn điện tử có sai sót gửi cơ quan thuế và lập biên bản điện tử để thỏa thuận với người mua trước khi thực hiện hủy hóa đơn."
        createCancellationReportSelect={
          selectedInvoice?.issueStatus ===
            INVOICE_STATUS.AUTHORITY_CODE_GIVEN &&
          [0, 1, 3].includes(selectedInvoice?.type) &&
          selectedInvoice?.reportStatus == 1
            ? () =>
                navigate(
                  `/hoa-don-dau-ra/hoa-don/bien-ban-huy/them-moi/${selectedInvoice?.invoiceId}`,
                )
            : null
        }
        createErrorAnnounceSelect={
          [1, 4, 6].includes(selectedInvoice?.invoiceErrAnnouceStatus)
            ? () =>
                navigate(
                  `/hoa-don-dau-ra/xu-ly-hoa-don/thong-bao-sai-sot/${selectedInvoice?.invoiceId}?invoiceClassify=MTT`,
                )
            : null
        }
        cancelInvoiceSelect={
          [0, 1].includes(selectedInvoice?.type)
            ? () => {
                dispatch(
                  setModalsState({
                    modalName: 'selectCancelationMethod',
                    status: false,
                  }),
                )
                dispatch(
                  setModalsState({
                    modalName: 'cancel',
                    status: true,
                  }),
                )
              }
            : null
        }
      /> */}
      <ModalSelectAdjustInvoiceMethod
        show={modalStates.selectAdjustInvoiceMethod}
        setShow={(status) =>
          dispatch(
            setModalsState({
              modalName: 'selectAdjustInvoiceMethod',
              status,
            }),
          )
        }
        header="Xác nhận"
        content="Bạn có thể lập biên bản điện tử để thỏa thuận với người mua trước khi lập hóa đơn điều chỉnh."
        createAdjustedReportSelect={
          // () => {
          //   console.log('reports', listInvoiceData?.reports)
          // }
          selectedInvoice?.issueStatus ===
            INVOICE_STATUS.AUTHORITY_CODE_GIVEN &&
          [0, 5].includes(selectedInvoice?.type) &&
          !findObjectInArray(
            listInvoiceData?.reports,
            'invoiceId',
            selectedInvoice?.invoiceId,
          ) &&
          selectedInvoice?.reportStatus == 1
            ? () =>
                navigate(
                  `/hoa-don-dau-ra/hoa-don/bien-ban-dieu-chinh/them-moi/${selectedInvoice?.invoiceId}`,
                )
            : null
        }
        createAdjustAnnounceSelect={
          [1, 4, 6].includes(selectedInvoice?.invoiceErrAnnouceStatus)
            ? () =>
                navigate(
                  `/hoa-don-dau-ra/xu-ly-hoa-don/thong-bao-sai-sot/${selectedInvoice?.invoiceId}?invoiceClassify=MTT`,
                )
            : null
        }
        adjustInvoiceSelect={
          [0, 5].includes(selectedInvoice?.type)
            ? () => {
                navigate(
                  `/hoa-don-dau-ra/xu-ly-hoa-don/may-tinh-tien/dieu-chinh/${selectedInvoice?.invoiceId}`,
                )
              }
            : null
        }
      />
      {/* <ModalConfirm
        header="Xác nhận"
        content="Bạn cần hủy hóa đơn bị sai sót trước khi lập hóa đơn thay thế. Bạn có muốn hủy hóa đơn này không?"
        show={modalStates.selectReplacementMethod}
        setShow={(status) =>
          dispatch(
            setModalsState({
              modalName: 'selectReplaceInvoiceMethod',
              status,
            }),
          )
        }
        handleConfirm={(e) => {
          e.preventDefault()
          dispatch(
            setModalsState({
              modalName: 'selectReplacementMethod',
              status: false,
            }),
          )
          dispatch(
            setModalsState({
              modalName: 'selectCancelationMethod',
              status: true,
            }),
          )
        }}
      /> */}

      <ModalSelectReplaceInvoiceMethod
        show={modalStates.selectReplacementMethod}
        setShow={(status) =>
          dispatch(
            setModalsState({
              modalName: 'selectReplacementMethod',
              status,
            }),
          )
        }
        header="Xác nhận"
        content="Bạn có thể lập biên bản điện tử để thỏa thuận với người mua trước khi lập hóa đơn thay thế."
        createReplacedReportSelect={
          selectedInvoice?.issueStatus ===
            INVOICE_STATUS.AUTHORITY_CODE_GIVEN &&
          [
            INVOICE_TYPE.REPLACED,
            INVOICE_TYPE.DELETED,
            INVOICE_TYPE.ORIGINAL,
          ].includes(selectedInvoice?.type) &&
          selectedInvoice?.reportStatus == 1
            ? () =>
                navigate(
                  `/hoa-don-dau-ra/hoa-don/bien-ban-thay-the/them-moi/${selectedInvoice?.invoiceId}`,
                )
            : null
        }
        createReplaceAnnounceSelect={
          [
            INVOICE_TYPE.REPLACE,
            INVOICE_TYPE.REPLACED,
            INVOICE_TYPE.DELETED,
          ].includes(selectedInvoice?.invoiceErrAnnouceStatus)
            ? () =>
                navigate(
                  `/hoa-don-dau-ra/xu-ly-hoa-don/thong-bao-sai-sot/${selectedInvoice?.invoiceId}?invoiceClassify=MTT`,
                )
            : null
        }
        replaceInvoiceSelect={
          [
            INVOICE_TYPE.ORIGINAL,
            INVOICE_TYPE.REPLACED,
            INVOICE_TYPE.REPLACE,
          ].includes(selectedInvoice?.type)
            ? () => {
                navigate(
                  `/hoa-don-dau-ra/xu-ly-hoa-don/may-tinh-tien/thay-the/${selectedInvoice?.invoiceId}`,
                )
              }
            : null
        }
      />

      <CancelInvoiceModal
        show={modalStates.cancel}
        onHide={() =>
          dispatch(
            setModalsState({
              modalName: 'cancel',
              status: false,
            }),
          )
        }
        title="Huỷ hoá đơn"
        invoice={selectedInvoice}
        buttonAgreeOnClick={(value) =>
          cancelInvoice({ ...value, invoiceId: selectedInvoice.invoiceId })
        }
      />
      <ModalConfirm
        header="Hoá đơn điều chỉnh"
        content="Hoá đơn này đã được lập hoá đơn điều chỉnh. Bạn có muốn tiếp tục lập hoá
      đơn điều chỉnh cho hoá đơn này không"
        show={isInvoiceAdjust}
        setShow={setIsInvoiceAdjust}
        handleConfirm={(e) => {
          e.preventDefault()
          setIsInvoiceAdjust(false)
          navigate(
            `/hoa-don-dau-ra/xu-ly-hoa-don/may-tinh-tien/dieu-chinh/${selectedInvoice?.invoiceId}`,
          )
        }}
      />
      <ModalConfirm
        show={hsmSignModalInfo.show}
        setShow={(value) =>
          setHSMSignModalInfo((old) => ({
            show: value,
            invoiceIds: !value ? false : old?.invoiceIds,
          }))
        }
        header="Ký số HSM"
        content="Chương trình sẽ thực hiện ký số HSM. Bạn chắc chắn vẫn ký?"
        handleConfirm={handleHsmSignSummaryInvoice}
      />
      <ModalConfirm
        show={restoreOriginModalConfig.show}
        setShow={(value) =>
          setRestoreOriginModalConfig((old) => ({ ...old, show: value }))
        }
        header="Xác nhận"
        content="Bạn có thực sự muốn khôi phục hóa đơn này về trạng thái Hóa đơn mới?"
        handleConfirm={() => restoreOrigin(restoreOriginModalConfig.invoiceId)}
        runWhenClosedFunc={() =>
          setRestoreOriginModalConfig((old) => ({ ...old, invoiceId: null }))
        }
      />
      <ModalUploadInvoiceExcel
        show={modalStates.upload}
        setShow={(status) =>
          dispatch(setModalsState({ modalName: 'upload', status }))
        }
        refetchListInvoice={refetchListInvoice}
        serials={serials}
      />
      <ModalConfirm
        header="Xác nhận"
        content="Chương trình nhận thấy bạn đang chọn nhiều hoá đơn với các khách hàng khác nhau. Bạn có chắc chắn muốn tiếp tục?"
        show={modalStates.detectMultipleCustomer}
        setShow={(status) =>
          dispatch(
            setModalsState({
              modalName: 'detectMultipleCustomer',
              status,
            }),
          )
        }
        handleConfirm={() => {
          dispatch(
            setModalsState({
              modalName: 'sentMail',
              status: true,
            }),
          )
        }}
      />
      <ModalSendEmail
        ref={modalSentMailRef}
        isOpen={modalStates.sentMail}
        selectedInvoices={selectedInvoices}
        onOpenChange={(status) =>
          dispatch(setModalsState({ modalName: 'sentMail', status }))
        }
      />

      <ModalSocketInvoiceSign
        show={findModalSocketInvoiceSign?.show}
        onHide={() => setFindModalSocketInvoiceSign({ show: false })}
        title="Lịch sử đối tượng"
        objectId={findModalSocketInvoiceSign?.objectId}
        createDateObject={findModalSocketInvoiceSign?.createDate}
        // invoiceType={INVOICE_TYPE_TO_QUERY.CAN_ERR_ANNOUCED}
        buttonAgreeOnlick={(selectedInvoices) => {
          setFindModalSocketInvoiceSign(false)

          setFindModalSocketInvoiceSign(false)
        }}
        buttonAgreeTab2Onlick={(selectedInvoice) => {
          setFindModalSocketInvoiceSign(false)
        }}
        onRowClicked={() => {}}
        showTab={false}
        defaultTab={0}
      />
    </ContentContainer>
  )
}
